import { useEffect, useRef, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import axios from "axios";
import "@/App.css";
import { translations, LanguageContext } from "@/i18n";
import { AuthProvider, useAuth } from "@/auth";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import EditorialMarquee from "@/components/EditorialMarquee";
import ChiSiamo from "@/components/ChiSiamo";
import Attivita from "@/components/Attivita";
import CosaPuoiFare from "@/components/CosaPuoiFare";
import News from "@/components/News";
import MissioneFaq from "@/components/MissioneFaq";
import Contatti from "@/components/Contatti";
import Footer from "@/components/Footer";
import DonateDialog from "@/components/DonateDialog";
import DonorArea from "@/components/DonorArea";
import AdozionePage from "@/components/AdozionePage";
import VolontariatoPage from "@/components/VolontariatoPage";
import NewsDetailPage from "@/components/NewsDetailPage";
import NewsAdminPage from "@/components/NewsAdminPage";
import { PaymentSuccess, PaymentCancel } from "@/components/PaymentResult";
import { Seo } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

function Home({ scrollTo, openDonate }) {
  return (
    <>
      <Seo
        path="/"
        title="You4Mada — Tu, per il Madagascar"
        description="ONG che porta acqua potabile, istruzione, cibo e cure nei villaggi del sud del Madagascar. Dona ora o adotta un bambino a distanza con 30€ al mese."
      />
      <Header scrollTo={scrollTo} onDonate={openDonate} />
      <main>
        <Hero scrollTo={scrollTo} onDonate={openDonate} />
        <EditorialMarquee />
        <ChiSiamo />
        <Attivita />
        <EditorialMarquee variant="alt" />
        <CosaPuoiFare onDonate={openDonate} scrollTo={scrollTo} />
        <MissioneFaq />
        <News />
        <Contatti />
      </main>
      <Footer scrollTo={scrollTo} />
    </>
  );
}

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("session_id");
    const exchange = async () => {
      try {
        const res = await axios.post(`${API}/auth/session`, { session_id: sessionId });
        setUser(res.data);
        navigate("/area-donatore", { replace: true, state: { user: res.data } });
      } catch {
        navigate("/", { replace: true });
      }
    };
    exchange();
  }, [navigate, setUser]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background" data-testid="auth-callback-loading">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  );
}

function AppRouter({ scrollTo, openDonate }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home scrollTo={scrollTo} openDonate={openDonate} />} />
      <Route path="/area-donatore" element={<DonorArea />} />
      <Route path="/adozione-a-distanza" element={<AdozionePage />} />
      <Route path="/volontariato" element={<VolontariatoPage />} />
      <Route path="/news/:id" element={<NewsDetailPage />} />
      <Route path="/admin" element={<NewsAdminPage />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
    </Routes>
  );
}

function App() {
  const [lang, setLang] = useState("it");
  const [donateOpen, setDonateOpen] = useState(false);
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (lenisRef.current) {
      lenisRef.current.scrollTo(el, { offset: -72 });
    } else {
      el.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const openDonate = useCallback(() => setDonateOpen(true), []);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t: translations[lang] }}>
      <AuthProvider>
        <div className="grain min-h-screen bg-background text-foreground">
          <BrowserRouter>
            <AppRouter scrollTo={scrollTo} openDonate={openDonate} />
          </BrowserRouter>
          <DonateDialog open={donateOpen} onClose={() => setDonateOpen(false)} />
          <Toaster position="bottom-center" richColors />
        </div>
      </AuthProvider>
    </LanguageContext.Provider>
  );
}

export default App;
